import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRightIcon, CheckCircleIcon } from "@heroicons/react/24/outline";
import { Link } from "react-router-dom";

// --------------------------------------------------------
// COUNT-UP COMPONENT
// --------------------------------------------------------
const CountUpNumber = ({ target }) => {
  const [value, setValue] = useState(0);

  useEffect(() => {
    let start = 0;
    const end = parseInt(target.replace(/[^0-9]/g, ""));
    const duration = 1200;
    const step = (end - start) / (duration / 16);

    const counter = setInterval(() => {
      start += step;
      if (start >= end) {
        clearInterval(counter);
        setValue(end);
      } else {
        setValue(Math.floor(start));
      }
    }, 16);

    return () => clearInterval(counter);
  }, [target]);

  return (
    <>
      {value}
      {target.includes("%") ? "%" : target.includes("+") ? "+" : ""}
    </>
  );
};

// --------------------------------------------------------
// MAIN HERO SECTION
// --------------------------------------------------------
const HeroSection = () => {
  const [phase, setPhase] = useState(1);

  // AUTO ROTATE STATS
  useEffect(() => {
    const interval = setInterval(() => {
      setPhase((prev) => (prev === 3 ? 1 : prev + 1));
    }, 4500);

    return () => clearInterval(interval);
  }, []);

  return (
    <section
      className="w-full bg-white pt-28 pb-20 relative overflow-hidden"
      style={{ fontFamily: "Poppins, Roboto, sans-serif" }}
    >
      {/* --------------------------------------------- */}
      {/* FLOATING BACKGROUND BUBBLES */}
      {/* --------------------------------------------- */}
      <motion.div
        className="absolute top-10 left-[-50px] w-40 h-40 bg-teal-200 rounded-full blur-3xl opacity-30"
        animate={{ y: [0, -35, 0] }}
        transition={{ repeat: Infinity, duration: 6 }}
      />
      <motion.div
        className="absolute bottom-[-40px] right-[-50px] w-56 h-56 bg-gray-300 rounded-full blur-3xl opacity-40"
        animate={{ y: [0, 30, 0] }}
        transition={{ repeat: Infinity, duration: 7 }}
      />

      <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center relative z-10">
        {/* --------------------------------------------- */}
        {/* LEFT CONTENT */}
        {/* --------------------------------------------- */}
        <div className="space-y-6">
          {/* HEADLINE */}
          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="text-2xl md:text-4xl font-bold text-gray-900 leading-snug"
          >
            A Smarter, Faster & Modern HR Experience  
            {/* <span className="block text-gray-800 font-extrabold mt-1">
              Built for GTPL’s People & HR Teams
            </span> */}
          </motion.h1>

          {/* SUBTEXT */}
          <motion.p
            initial={{ opacity: 0, y: 25 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.6 }}
            className="text-gray-600 text-sm md:text-base max-w-md leading-relaxed"
            style={{ fontFamily: "Roboto, sans-serif" }}
          >
            A unified HRMS crafted exclusively By GTPL — fast, reliable,
            automated and beautifully simple. From attendance to payroll,
            everything flows seamlessly.
          </motion.p>

          {/* FEATURES */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.25 }}
            className="space-y-1.5"
          >
            {[
              "GPS Attendance & Geo-Tag Punch",
              "Automated Payroll & Compliance",
              "Performance & Goal Tracking",
              "Onboarding, Training & LMS",
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.45 + index * 0.1 }}
                className="flex items-center text-gray-700 text-sm md:text-[15px]"
              >
                <CheckCircleIcon className="w-4 h-4 text-teal-600 mr-2" />
                {item}
              </motion.div>
            ))}
          </motion.div>

          {/* CTA BUTTON */}
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
          >
            <Link
              to="/register"
              className="inline-flex items-center px-7 py-3 bg-gray-900 text-white text-sm font-medium rounded-md shadow-md hover:bg-black transition-all"
            >
              Get Started
              <ArrowRightIcon className="w-4 h-4 ml-2" />
            </Link>
          </motion.div>
        </div>

        {/* --------------------------------------------- */}
        {/* RIGHT CONTENT — PHASED STATS */}
        {/* --------------------------------------------- */}

        <div className="relative h-60 md:h-72 flex items-center justify-center select-none">
          <AnimatePresence mode="wait">
            {/* ---------------- Phase 1 ---------------- */}
            {phase === 1 && (
              <motion.div
                key="phase1"
                initial={{ opacity: 0, scale: 0.92 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.4 }}
                className="grid grid-cols-2 gap-6 text-center"
              >
                {[
                  { v: "850+", l: "Employees Managed" },
                  { v: "12+", l: "HR Processes Automated" },
                  { v: "99.9%", l: "Uptime & Reliability" },
                  { v: "100%", l: "Paperless Onboarding" },
                ].map((s, i) => (
                  <div
                    key={i}
                    className="bg-gray-100 rounded-xl p-4 shadow-sm hover:shadow-md transition"
                  >
                    <p className="text-xl font-bold text-gray-900">
                      <CountUpNumber target={s.v} />
                    </p>
                    <p className="text-xs text-gray-600 mt-1">{s.l}</p>
                  </div>
                ))}
              </motion.div>
            )}

            {/* ---------------- Phase 2 ---------------- */}
            {phase === 2 && (
              <motion.div
                key="phase2"
                initial={{ opacity: 0, scale: 0.92 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.4 }}
                className="grid grid-cols-2 gap-6"
              >
                {[
                  { p: 85, label: "Automation" },
                  { p: 99, label: "Accuracy" },
                  { p: 92, label: "Engagement" },
                  { p: 78, label: "Adoption Rate" },
                ].map((s, i) => (
                  <div key={i} className="flex flex-col items-center">
                    <div className="relative w-20 h-20">
                      <svg className="w-full h-full">
                        <circle
                          cx="40"
                          cy="40"
                          r="30"
                          stroke="#e5e7eb"
                          strokeWidth="8"
                          fill="transparent"
                        />
                        <motion.circle
                          cx="40"
                          cy="40"
                          r="30"
                          stroke="#0f766e"
                          strokeWidth="8"
                          fill="transparent"
                          strokeDasharray="188"
                          strokeDashoffset={188 - (188 * s.p) / 100}
                          initial={{ strokeDashoffset: 188 }}
                          animate={{
                            strokeDashoffset:
                              188 - (188 * s.p) / 100,
                          }}
                          transition={{ duration: 1 }}
                        />
                      </svg>
                      <span className="absolute inset-0 flex items-center justify-center text-sm font-semibold text-gray-900">
                        {s.p}%
                      </span>
                    </div>
                    <p className="text-xs mt-2 text-gray-600">{s.label}</p>
                  </div>
                ))}
              </motion.div>
            )}

            {/* ---------------- Phase 3 ---------------- */}
            {phase === 3 && (
              <motion.div
                key="phase3"
                initial={{ opacity: 0, scale: 0.92 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.4 }}
                className="grid grid-cols-2 gap-5"
              >
                {[
                  { val: "6x", label: "Faster Payroll" },
                  { val: "4x", label: "Less Manual Work" },
                  { val: "3x", label: "More Accuracy" },
                  { val: "5x", label: "Better Experience" },
                ].map((s, i) => (
                  <motion.div
                    key={i}
                    whileHover={{ scale: 1.05 }}
                    className="bg-gray-100 rounded-xl p-4 shadow-sm text-center hover:shadow-md transition"
                  >
                    <p className="text-xl font-bold text-gray-900">
                      {s.val}
                    </p>
                    <p className="text-xs text-gray-600">{s.label}</p>
                  </motion.div>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
